import type { ReadBranchType } from '@readme/api/src/mappings/branch/types';

import React, { useCallback, useMemo } from 'react';

import useClassy from '@core/hooks/useClassy';
import { useSuperHubStore, useAPIDesignerStore, useProjectStore } from '@core/store';
import { isReferencePage, isChangelog, isGuidesPage, isCustomPage } from '@core/store/SuperHub/Document/util';
import { isBranch, isVersion } from '@core/store/SuperHub/util';

import CreateBranch, { CreateBranchButton } from '@routes/SuperHub/Versions/CreateBranch';

import Button from '@ui/Button';
import Flex from '@ui/Flex';
import Icon from '@ui/Icon';

import { useSuperHubEditorFormContext } from '../../Context';
import useEditorFormOnSubmit from '../../Root/useEditorFormOnSubmit';

import styles from './index.module.scss';

/**
 * The bottom sticky footer which contains form actions like "Save".
 */
export default function LayoutFooter() {
  const bem = useClassy(styles, 'SuperHubEditorFormLayoutFooter');

  const apiDesignerErrors = useAPIDesignerStore(s => s.errors);
  const planHasBranches = useProjectStore(s => s.plan.features.branches);
  const [currentBranch, updateEphemeralBranch, isSavingDisabled, layout] = useSuperHubStore(s => [
    s.currentBranch,
    s.branches.updateEphemeralBranch,
    s.editor.isSavingDisabled,
    s.layout,
  ]);
  const {
    trigger,
    formState: { isDirty, isSubmitting, defaultValues, isValid },
  } = useSuperHubEditorFormContext();

  const isViewingChangelog = isChangelog(defaultValues);
  const isViewingGuides = isGuidesPage(defaultValues);
  const isViewingCustomPage = isCustomPage(defaultValues);
  const isViewingReference = isReferencePage(defaultValues);
  const hasApiDesignerError = useMemo(
    () => isReferencePage(defaultValues) && !!Object.keys(apiDesignerErrors).length,
    [apiDesignerErrors, defaultValues],
  );

  const isSubmitDisabled = isSavingDisabled || !isDirty || hasApiDesignerError;
  const onSubmit = useEditorFormOnSubmit();

  const onCreateBranchSuccess = useCallback(
    ({ data }: ReadBranchType) => {
      return onSubmit(undefined, data.name);
    },
    [onSubmit],
  );

  const isAiLayout = ['ai'].includes(layout);
  const handleOpenAIPanel = useCallback(() => {
    window.location.hash = '/ai';
  }, []);
  const showAiAgent = useMemo(() => {
    return isViewingGuides || isViewingCustomPage || isViewingReference;
  }, [isViewingGuides, isViewingCustomPage, isViewingReference]);

  return (
    <Flex align="center" className={bem('&')} gap="sm" justify={!!showAiAgent && !isAiLayout ? 'space-between' : 'end'}>
      {!!showAiAgent && !isAiLayout && (
        <Button aria-label="Open AI Chat" ghost kind="minimum" onClick={handleOpenAIPanel} size="sm">
          <Icon aria-hidden="true" name="message-square" />
          AI Agent
        </Button>
      )}

      <Flex align="center" gap="sm">
        {!!isViewingChangelog && !!isBranch(currentBranch) && (
          <Flex align="center" className={bem('-warning')} gap="xs">
            <Icon aria-hidden="true" className={bem('-warning-icon')} name="info" /> Changelogs aren’t versioned. Any
            changes are live immediately.
          </Flex>
        )}

        {!isViewingChangelog && isVersion(currentBranch) && !!planHasBranches && (
          <CreateBranch onSuccess={onCreateBranchSuccess} source="editor" version={currentBranch}>
            <CreateBranchButton
              aria-label="Save to Branch"
              disabled={isSubmitDisabled}
              kind="minimum"
              onClick={() => {
                // when the user starts the create branch flow, trigger
                // revalidation on the entire form and focus on the first error
                trigger(undefined, { shouldFocus: true });
                // if the form is not valid, close the create branch flow and
                // return the user to the editor where the invalid fields will
                // be highlighted
                if (!isValid) {
                  updateEphemeralBranch(null);
                }
              }}
              outline
              size="sm"
            >
              Save to Branch
            </CreateBranchButton>
          </CreateBranch>
        )}

        <Button disabled={isSubmitDisabled} loading={isSubmitting} size="sm" type="submit">
          Save
        </Button>
      </Flex>
    </Flex>
  );
}
